import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import payrollService from '@/services/payrollService'

const initialState = {
  records: [],
  currentRecord: null,
  statistics: null,
  salaryStructure: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    perPage: 20,
    totalPages: 0,
  },
}

// Async thunks
export const calculatePayroll = createAsyncThunk(
  'payroll/calculate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await payrollService.calculatePayroll(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to calculate payroll')
    }
  }
)

export const generatePayroll = createAsyncThunk(
  'payroll/generate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await payrollService.generatePayroll(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate payroll')
    }
  }
)

export const fetchRecords = createAsyncThunk(
  'payroll/fetchRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await payrollService.getRecords(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll records')
    }
  }
)

export const fetchRecord = createAsyncThunk(
  'payroll/fetchRecord',
  async (id, { rejectWithValue }) => {
    try {
      const response = await payrollService.getRecord(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll record')
    }
  }
)

export const approveRecord = createAsyncThunk(
  'payroll/approve',
  async (id, { rejectWithValue }) => {
    try {
      const response = await payrollService.approveRecord(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve payroll')
    }
  }
)

export const markPaid = createAsyncThunk(
  'payroll/markPaid',
  async (id, { rejectWithValue }) => {
    try {
      const response = await payrollService.markPaid(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark as paid')
    }
  }
)

export const batchApprove = createAsyncThunk(
  'payroll/batchApprove',
  async (recordIds, { rejectWithValue }) => {
    try {
      const response = await payrollService.batchApprove(recordIds)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to batch approve')
    }
  }
)

export const fetchStatistics = createAsyncThunk(
  'payroll/fetchStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await payrollService.getStatistics(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch statistics')
    }
  }
)

export const fetchSalaryStructure = createAsyncThunk(
  'payroll/fetchSalaryStructure',
  async (params, { rejectWithValue }) => {
    try {
      const response = await payrollService.getSalaryStructure(params)
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch salary structure'
      )
    }
  }
)

const payrollSlice = createSlice({
  name: 'payroll',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Calculate Payroll
      .addCase(calculatePayroll.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(calculatePayroll.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(calculatePayroll.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Generate Payroll
      .addCase(generatePayroll.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(generatePayroll.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(generatePayroll.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Records
      .addCase(fetchRecords.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        state.loading = false
        state.records = action.payload.data.items
        state.pagination = action.payload.data.pagination
      })
      .addCase(fetchRecords.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Single Record
      .addCase(fetchRecord.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchRecord.fulfilled, (state, action) => {
        state.loading = false
        state.currentRecord = action.payload.data
      })
      .addCase(fetchRecord.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Approve
      .addCase(approveRecord.fulfilled, (state) => {
        state.loading = false
      })
      // Mark Paid
      .addCase(markPaid.fulfilled, (state) => {
        state.loading = false
      })
      // Batch Approve
      .addCase(batchApprove.fulfilled, (state) => {
        state.loading = false
      })
      // Fetch Statistics
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload.data
      })
      // Fetch Salary Structure
      .addCase(fetchSalaryStructure.fulfilled, (state, action) => {
        state.salaryStructure = action.payload.data
      })
  },
})

export const { clearError, clearCurrentRecord } = payrollSlice.actions
export default payrollSlice.reducer
